import React from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { RMCard } from '../RMCard';
import { Text } from '../../../components';
import { CardTrendPercentage } from '../CardTrendPercentage/CardTrendPercentage';
import { primaryTrendState } from '../../SummaryWrapper/inventorySummaryHelpers';
import classNames from 'classnames';


import { LoadableValue, disabledWhileLoading, useCardLoadingState, SKELETON_WIDTH } from '../CardLoadingUtils';
import './UtilizationInventoryCard.scss';

export const UtilizationInventoryCard = ({
    title,
    subLabel,
    subLabelIcon,
    subLabelIconClass = '',
    headerStatIcon,
    headerStatLabel,
    headerStatValue,
    primaryValue,
    primaryPercent,
    primaryTrend,
    description,
    rows = [],
    variant = null,
    className = '',
    isExpanded = true,
    onMoreClick,
    onPrimaryClick,
    queryState,
    ...props
}) => {
    const { loading, isError, disabled } = useCardLoadingState(queryState);
    const getRowClassName = (row) =>
        classNames('rm-utilization-card-row', {
            'rm-utilization-card-row--clickable': row.onClick && !disabled,
        });
    const primaryLeftClassName = classNames('rm-utilization-card-left', {
        'rm-utilization-card-left--clickable': onPrimaryClick && !disabled,
    });
    const collapsedMetricClassName = classNames('rm-utilization-card-metric', {
        'rm-utilization-card-left--clickable': onPrimaryClick && !disabled,
    });

    return (
        <RMCard
            variant={variant}
            className={classNames('rm-utilization-card', className, { collapsed: !isExpanded })}
            showMoreIcon={false}
            isError={isError}
            {...props}
        >
            <header>
                <Text size="large" color="secondary">
                    {title}
                </Text>
                {isExpanded && headerStatLabel && (
                    <span className="rm-utilization-card-header-stat">
                        {headerStatIcon && <span className="rm-utilization-card-header-icon">{headerStatIcon}</span>}
                        <Text size="small">{headerStatLabel}:</Text>
                        <Text size="small" semiBold>
                            {headerStatValue}
                        </Text>
                    </span>
                )}
                {/* <MoreOutlined className="rm-utilization-card-more-icon" onClick={onMoreClick} /> */}
            </header>
            {isExpanded ? (
                <div className="rm-utilization-card-body">
                    <div
                        className={primaryLeftClassName}
                        onClick={disabledWhileLoading(disabled, onPrimaryClick)}
                    >
                        <span className="rm-utilization-card-center-group">
                            {subLabel && (
                                <span className="rm-utilization-card-sub-label">
                                    {subLabelIcon && (
                                        <span
                                            className={classNames(
                                                'rm-utilization-card-sub-label-icon',
                                                subLabelIconClass,
                                            )}
                                        >
                                            {subLabelIcon}
                                        </span>
                                    )}
                                    <Text size="small" color="muted" bold>
                                        {subLabel}
                                    </Text>
                                </span>
                            )}
                            <span className="rm-utilization-card-metric">
                                <Text size="extraLarge" color="primaryBlue">
                                    <LoadableValue isLoading={loading} width={SKELETON_WIDTH.large}>{primaryValue}</LoadableValue>
                                </Text>
                                {primaryPercent && !loading && (
                                    <CardTrendPercentage
                                        status={primaryTrendState(primaryTrend)}
                                        arrowDirection={primaryTrend}
                                        percentValue={primaryPercent}
                                    />
                                )}
                            </span>
                            {description && (
                                <Text size="small" color="light" className="rm-utilization-card-description">
                                    {description}
                                </Text>
                            )}
                        </span>
                    </div>
                    {rows.length > 0 && (
                        <div className="rm-utilization-card-right">
                            {rows.map((row, idx) => (
                                <span
                                    key={idx}
                                    className={getRowClassName(row)}
                                    onClick={disabledWhileLoading(disabled, row.onClick)}
                                >
                                    {row.icon ? (
                                        <span
                                            className={classNames(
                                                'rm-utilization-card-row-icon',
                                                row.iconVariant ?? row.variant,
                                            )}
                                        >
                                            {row.icon}
                                        </span>
                                    ) : (
                                        <span className="rm-utilization-card-row-icon-placeholder" />
                                    )}
                                    <span className="rm-utilization-card-row-content">
                                        <Text size="small" color="muted" bold>
                                            {row.label}
                                        </Text>
                                        <span className="rm-utilization-card-row-values">
                                            <Text
                                                color="primaryBlue"
                                                className={classNames(
                                                    'rm-card-primary',
                                                    'rm-utilization-card-row-count',
                                                )}
                                            >
                                                <LoadableValue isLoading={loading}>{row.count}</LoadableValue>
                                            </Text>
                                            {row.countSuffix && !loading && (
                                                <Text
                                                    size="small"
                                                    color="secondary"
                                                    className="rm-utilization-card-row-unit"
                                                >
                                                    {row.countSuffix}
                                                </Text>
                                            )}
                                            {row.percent && !loading && (
                                                <CardTrendPercentage
                                                    className={classNames(
                                                        'rm-utilization-card-row-percent',
                                                        row.oversizedUndersizedState,
                                                    )}
                                                    status={row.oversizedUndersizedState ?? row.variant ?? 'default'}
                                                    arrowDirection={row.percentTrend}
                                                    percentValue={row.percent}
                                                />
                                            )}
                                        </span>
                                    </span>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <span
                    className={collapsedMetricClassName}
                    onClick={disabledWhileLoading(disabled, onPrimaryClick)}
                >
                    <Text
                        className={classNames('rm-card-primary', primaryTrendState(primaryTrend), 'collapsed-primary')}
                    >
                        {primaryValue}
                    </Text>
                    {primaryPercent && (
                        <CardTrendPercentage
                            status={primaryTrendState(primaryTrend)}
                            arrowDirection={primaryTrend}
                            percentValue={primaryPercent}
                        />
                    )}
                </span>
            )}
        </RMCard>
    );
};

UtilizationInventoryCard.displayName = 'UtilizationInventoryCard';
